<template>
  <v-dialog
    v-model="dialog"
    max-width="500px"
  >
    <v-card>
      <v-card-title>
        <span class="headline">
          Edit provider rate
        </span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <v-autocomplete
                v-model="formData.sourceCurrency"
                :items="currencies"
                label="Source Currency"
                disabled
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-autocomplete
                v-model="formData.destinationCurrency"
                :items="currencies"
                label="Destination Currency"
                disabled
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="formData.payoutPartnerName"
                label="Payout Partner Name"
                disabled
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-select
                v-model="formData.serviceCode"
                :items="serviceCodeOptions"
                item-text="label"
                item-value="value"
                label="Service Code"
                :error-messages="serviceCodeError"
                @change="serviceCodeError = ''"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model.number="formData.rate"
                label="Rate"
                type="number"
                min="0"
                step="0.0001"
                @input="onRateInput('rate')"
                @blur="onRateBlur('rate')"
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-autocomplete
                v-model="formData.sendingCountryIso3"
                :items="sendingCountries"
                item-text="name"
                item-value="codeIso3"
                label="Sending Country (optional, all if empty)"
                clearable
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <DateField
                v-model="formData.dueDate"
                label="Due Date (optional)"
                :min="minDueDate"
                :error-messages="dueDateError"
                clearable
                @input="dueDateError = ''"
              />
            </v-col>
            <v-col cols="12">
              <v-switch
                v-model="formData.rangesEnabled"
                inset
                label="Enable rate ranges"
                @change="onToggleRanges"
              />
            </v-col>
            <v-col
              v-if="formData.rangesEnabled"
              cols="12"
            >
              <div
                v-for="(range, index) in formData.rateRanges"
                :key="`rate-range-edit-${index}`"
                class="rate-range-card"
              >
                <v-btn
                  v-if="index > 0"
                  class="rate-range-card__remove"
                  icon
                  plain
                  x-small
                  @click="onRemoveRange(index)"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      v-model.number="range.minAmount"
                      type="number"
                      min="0"
                      label="Minimum amount"
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      v-model.number="range.rate"
                      type="number"
                      min="0"
                      label="Rate"
                      step="0.0001"
                      @input="onRangeRateInput(index)"
                      @blur="onRangeRateBlur(index)"
                    />
                  </v-col>
                </v-row>
              </div>
              <v-btn
                v-if="formData.rateRanges.length < rangesLimit"
                class="mt-2"
                color="primary"
                outlined
                block
                @click="onAddRange"
              >
                <v-icon left>
                  mdi-plus
                </v-icon>
                Add rate range
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-alert
        v-if="errorMessage"
        type="error"
        dense
        class="mx-4 mb-2"
      >
        {{ errorMessage }}
      </v-alert>

      <v-card-actions>
        <v-spacer />
        <v-btn @click="onClose">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="loading"
          @click="onSubmit"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// components
import DateField from "@/components/DateField";

// modules
import { mapGetters } from "vuex";

// utils
import { rateCurrencies } from "@/utils/staticAutopricing";

export default {
  name: "ModalEditProviderRate",

  components: {
    DateField
  },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    const ranges = Array.isArray(this.data.rateRanges)
      ? this.data.rateRanges.map(range => ({ ...range }))
      : [];

    return {
      formData: {
        ...this.data,
        rangesEnabled: Boolean(this.data.rangesEnabled) && ranges.length > 0,
        rateRanges: ranges,
        dueDate: this.data.dueDate || null,
        sendingCountryIso3: this.data.sendingCountryIso3 || null
      },
      serviceCodeError: '',
      dueDateError: '',
      errorMessage: '',
      loading: false,
      currencies: rateCurrencies,
      serviceCodeOptions: [
        { value: 'CSH', label: 'CSH - Cash Pickup' },
        { value: 'BNK', label: 'BNK - Bank Account' },
        { value: 'WLT', label: 'WLT - Mobile Wallet' },
        { value: 'CRD', label: 'CRD - Card Payment' },
        { value: 'HOM', label: 'HOM - Home Delivery' }
      ],
      rangesLimit: 50
    };
  },

  computed: {
    ...mapGetters({
      sendingCountries: "ActiveCountryStore/getListSourceCountries"
    }),

    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },

    minDueDate () {
      return this.$moment().add(10, 'days').format('YYYY-MM-DD');
    }
  },

  created() {
    if (!this.sendingCountries || !this.sendingCountries.length) {
      this.$store.dispatch("ActiveCountryStore/getList");
    }
    if (this.formData.rangesEnabled && this.formData.rateRanges.length === 0) {
      this.onAddRange();
    }
  },

  methods: {
    onSubmit () {
      if (!this.formData.serviceCode) {
        this.serviceCodeError = 'Service Code is required';
        return;
      }
      if (this.formData.dueDate && this.formData.dueDate < this.minDueDate) {
        this.dueDateError = `Due date must be at least 10 days from today (earliest: ${this.minDueDate})`;
        return;
      }
      this.loading = true;
      this.errorMessage = '';

      const data = this.buildPayload();
      this.$store
        .dispatch("ProviderRatesStore/updateProviderRate", data)
        .then(() => {
          this.$emit("save");
          this.onClose();
        })
        .catch(error => {
          this.errorMessage = error?.response?.data?.message || 'An unexpected error occurred. Please try again.';
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onToggleRanges (value) {
      if (value && this.formData.rateRanges.length === 0) {
        this.onAddRange();
      }
    },

    onAddRange () {
      if (this.formData.rateRanges.length >= this.rangesLimit) {
        return;
      }
      this.formData.rateRanges.push({
        minAmount: 0,
        rate: null
      });
    },

    onRemoveRange (index) {
      this.formData.rateRanges.splice(index, 1);
    },

    buildPayload () {
      const payload = {
        ...this.formData,
        rate: this.toRateNumber(this.formData.rate),
        sendingCountryIso3: this.formData.sendingCountryIso3 || null
      };

      if (!payload.rangesEnabled) {
        payload.rateRanges = [];
        payload.rangesEnabled = false;
        return payload;
      }

      const sanitizedRanges = this.formData.rateRanges
        .filter(range =>
          range != null &&
          range.minAmount !== '' &&
          range.rate !== '' &&
          range.minAmount !== null &&
          range.rate !== null
        )
        .map(range => ({
          minAmount: this.toAmountNumber(range.minAmount),
          rate: this.toRateNumber(range.rate)
        }))
        .filter(range => !Number.isNaN(range.minAmount) && !Number.isNaN(range.rate));

      sanitizedRanges.sort((a, b) => a.minAmount - b.minAmount);

      payload.rateRanges = sanitizedRanges;
      payload.rangesEnabled = sanitizedRanges.length > 0;

      this.formData.rateRanges = sanitizedRanges.map(range => ({ ...range }));
      this.formData.rangesEnabled = payload.rangesEnabled;

      if (!payload.rangesEnabled) {
        payload.rateRanges = [];
      }

      return payload;
    },

    toNumber (value) {
      if (value === '' || value === null || value === undefined) {
        return NaN;
      }
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : NaN;
    },

    toRateNumber (value, digits = 4) {
      const numeric = this.toNumber(value);
      if (Number.isNaN(numeric)) {
        return 0;
      }
      const factor = Math.pow(10, digits);
      return Math.round(numeric * factor) / factor;
    },

    toAmountNumber (value, digits = 2) {
      const numeric = this.toNumber(value);
      if (Number.isNaN(numeric)) {
        return 0;
      }
      const factor = Math.pow(10, digits);
      return Math.round(numeric * factor) / factor;
    },

    onClose () {
      this.dialog = false;
    },

    onRateInput (field) {
      this.$set(this.formData, field, this.limitDecimals(this.formData[field], 4));
    },

    onRateBlur (field) {
      this.$set(this.formData, field, this.toRateNumber(this.formData[field]));
    },

    onRangeRateInput (index) {
      if (!this.formData.rateRanges[index]) return;
      this.$set(
        this.formData.rateRanges,
        index,
        {
          ...this.formData.rateRanges[index],
          rate: this.limitDecimals(this.formData.rateRanges[index].rate, 4)
        }
      );
    },

    onRangeRateBlur (index) {
      if (!this.formData.rateRanges[index]) return;
      this.$set(
        this.formData.rateRanges,
        index,
        {
          ...this.formData.rateRanges[index],
          rate: this.toRateNumber(this.formData.rateRanges[index].rate)
        }
      );
    },

    limitDecimals (value, digits) {
      if (value === '' || value === null || value === undefined) {
        return value;
      }
      const stringValue = String(value);
      const [intPart, decimalPart] = stringValue.split('.');
      if (!decimalPart || decimalPart.length <= digits) {
        return stringValue;
      }
      return `${intPart}.${decimalPart.slice(0, digits)}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.rate-range-card {
  position: relative;
  margin-bottom: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding: 8px 12px;

  &__remove {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 2;
  }

  .v-input {
    margin: 0;
  }

  .v-input__slot {
    margin-bottom: 0;
  }
}
</style>
